import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import colors from "../../colors/color";
import filterIcon from "../../assets/img/filter_icon.svg";

const FilterDialog = ({ open, onClose, filters, setFilters, onApply, postQuiz, steps }) => {
    const handleStepChange = (step) => {
        if (!setFilters) return;
        setFilters({
            ...filters,
            step: filters.step === step ? null : step
        });
    };

    const handleQuizAnswerChange = (questionId, value, isMultiple = false) => {
        if (!setFilters) return;
        console.log(`🎯 FilterDialog - Setting filter: Question ID=${questionId} (type: ${typeof questionId}), Value="${value}"`);
        const currentAnswers = { ...(filters?.quiz_answers || {}) };

        if (isMultiple) {
            const currentValues = currentAnswers[questionId] || [];
            if (currentValues.includes(value)) {
                currentAnswers[questionId] = currentValues.filter(v => v !== value);
            } else {
                currentAnswers[questionId] = [...currentValues, value];
            }
        } else {
            if (currentAnswers[questionId] === value) {
                delete currentAnswers[questionId];
            } else {
                currentAnswers[questionId] = value;
            }
        }

        setFilters({
            ...filters,
            quiz_answers: currentAnswers
        });
    };


    const handleReset = () => {
        if (!setFilters) return;
        setFilters({
            quiz_answers: {},
            step: null
        });
    };

    const renderQuizFilters = () => {
        console.log('🔍 FilterDialog - postQuiz:', postQuiz);

        if (!postQuiz) {
            console.log('⚠️ FilterDialog - postQuiz is null/undefined');
            return null;
        }

        if (!postQuiz.questions) {
            console.log('⚠️ FilterDialog - postQuiz.questions is missing');
            console.log('📋 postQuiz structure:', Object.keys(postQuiz));
            return null;
        }

        console.log('📝 FilterDialog - Total questions:', postQuiz.questions.length);

        // Filter questions that are marked as filterable
        // or if no filterable property exists, use type-based filtering
        const filterableQuestions = postQuiz.questions.filter(q => {
            // If the question has an explicit 'filterable' property, use it
            if (q.hasOwnProperty('filterable')) {
                return q.filterable === true;
            }
            // Otherwise, filter by supported types (excluding quiz owner/metadata)
            return ['radio', 'checkbox', 'select', 'multi-select'].includes(q.type);
        });

        console.log('✅ FilterDialog - Filterable questions:', filterableQuestions.length);
        if (filterableQuestions.length > 0) {
            console.log('📊 Filterable questions:', filterableQuestions.map(q => ({ id: q.id, text: q.text, type: q.type })));
        }

        if (filterableQuestions.length === 0) return null;

        return (
            <Box sx={{ mt: 3 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontFamily: 'Poppins',
                        mb: 2,
                        color: colors.primary,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&::before': {
                            content: '""',
                            display: 'block',
                            width: '4px',
                            height: '20px',
                            bgcolor: colors.primary,
                            borderRadius: '2px'
                        }
                    }}
                >
                    Questions du Quiz
                </Typography>
                {filterableQuestions.map((q, idx) => {
                    const questionId = q.id || `question_${idx}`;
                    const questionText = q.text || q.label || q.question || 'Question sans titre';
                    const questionType = q.type || 'text';

                    // Choice-based questions (radio, checkbox, select, multi-select)
                    if (['radio', 'checkbox', 'select', 'multi-select'].includes(questionType)) {
                        const isMultiple = ['checkbox', 'multi-select'].includes(questionType);
                        const options = q.options || q.choices || [];

                        return (
                            <Box key={questionId} sx={{ mb: 3 }}>
                                <Typography sx={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 500, mb: 1.5 }}>
                                    {questionText}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {options.map((opt, optIdx) => {
                                        const optionValue = opt.id || opt.value || opt.text || opt.label || opt;
                                        const optionLabel = opt.text || opt.label || opt.value || opt;
                                        const isSelected = isMultiple
                                            ? (filters?.quiz_answers?.[questionId] || []).includes(optionValue)
                                            : filters?.quiz_answers?.[questionId] === optionValue;

                                        return (
                                            <Button
                                                key={optIdx}
                                                size="small"
                                                variant={isSelected ? "contained" : "outlined"}
                                                onClick={() => handleQuizAnswerChange(questionId, optionValue, isMultiple)}
                                                sx={{
                                                    fontFamily: 'Poppins',
                                                    textTransform: 'none',
                                                    borderRadius: '12px',
                                                    fontSize: '13px',
                                                    fontWeight: isSelected ? 600 : 400,
                                                    py: 0.8,
                                                    px: 2.5,
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    ...(isSelected ? {
                                                        bgcolor: colors.primary,
                                                        color: '#fff',
                                                        // boxShadow: `0 4px 12px ${colors.primary}33`,
                                                        // '&:hover': {
                                                        //     bgcolor: colors.primary,
                                                        //     opacity: 0.9,
                                                        //     transform: 'translateY(-1px)',
                                                        //     boxShadow: `0 6px 16px ${colors.primary}4D`,
                                                        // }
                                                    } : {
                                                        color: '#666',
                                                        borderColor: '#e0e0e0',
                                                        bgcolor: '#fafafa',
                                                        '&:hover': {
                                                            borderColor: colors.primary,
                                                            color: colors.primary,
                                                            bgcolor: '#fff',
                                                            transform: 'translateY(-1px)',
                                                        }
                                                    })
                                                }}
                                            >
                                                {optionLabel}
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    }


                    return null;
                })}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    // boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: '1.5rem',
                pt: 3,
                pb: 1,
                color: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '12px',
                            bgcolor: `${colors.primary}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <img src={filterIcon} alt="Filter" style={{ width: 20, height: 20 }} />
                    </Box>
                    Filtrer les candidats
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: '#999' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 3, py: 2 }}>
                <Box sx={{ mt: 1 }}>
                    {/* Candidate Step Filter */}
                    {steps && steps.length > 0 && (
                        <>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontFamily: 'Poppins',
                                    mb: 2,
                                    color: colors.primary,
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    '&::before': {
                                        content: '""',
                                        display: 'block',
                                        width: '4px',
                                        height: '20px',
                                        bgcolor: colors.primary,
                                        borderRadius: '2px'
                                    }
                                }}
                            >
                                Étape du candidat
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                                {steps.map((step, idx) => (
                                    <Button
                                        key={idx}
                                        size="small"
                                        variant={filters?.step === step ? "contained" : "outlined"}
                                        onClick={() => handleStepChange(step)}
                                        sx={{
                                            fontFamily: 'Poppins',
                                            textTransform: 'none',
                                            borderRadius: '12px',
                                            fontSize: '13px',
                                            py: 0.8,
                                            px: 2.5,
                                            fontWeight: filters?.step === step ? 600 : 400,
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            ...(filters?.step === step ? {
                                                bgcolor: colors.primary,
                                                color: '#fff',
                                                // boxShadow: `0 4px 12px ${colors.primary}33`,
                                                // '&:hover': {
                                                //     bgcolor: colors.primary,
                                                //     opacity: 0.9,
                                                //     transform: 'translateY(-1px)',
                                                //     boxShadow: `0 6px 16px ${colors.primary}4D`,
                                                // }
                                            } : {
                                                color: '#666',
                                                borderColor: '#e0e0e0',
                                                bgcolor: '#fafafa',
                                                '&:hover': {
                                                    borderColor: colors.primary,
                                                    color: colors.primary,
                                                    bgcolor: '#fff',
                                                    transform: 'translateY(-1px)',
                                                }
                                            })
                                        }}
                                    >
                                        {step}
                                    </Button>
                                ))}
                            </Box>
                        </>
                    )}

                    {/* Quiz Question Filters */}
                    {renderQuizFilters()}

                    {/* Show message if no filters available */}
                    {(!steps || steps.length === 0) && (!postQuiz || !postQuiz.questions || postQuiz.questions.filter(q => q.hasOwnProperty('filterable') ? q.filterable === true : ['radio', 'checkbox', 'select', 'multi-select'].includes(q.type)).length === 0) && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography sx={{ fontFamily: 'Poppins', color: '#999', fontSize: '14px', mb: 1 }}>
                                Aucun filtre disponible
                            </Typography>
                            <Typography sx={{ fontFamily: 'Poppins', color: '#bbb', fontSize: '12px' }}>
                                {!postQuiz ? 'Quiz non chargé' : !postQuiz.questions ? 'Aucune question trouvée' : 'Aucune question filtrable'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 4, pt: 2, gap: 2 }}>
                <Button
                    onClick={handleReset}
                    sx={{
                        fontFamily: 'Poppins',
                        textTransform: 'none',
                        color: '#666',
                        fontSize: '15px',
                        fontWeight: 500,
                        '&:hover': { color: colors.primary, bgcolor: `${colors.primary}10` }
                    }}
                >
                    Réinitialiser
                </Button>
                <Button
                    onClick={onApply}
                    variant="contained"
                    fullWidth
                    sx={{
                        fontFamily: 'Poppins',
                        textTransform: 'none',
                        borderRadius: '14px',
                        bgcolor: colors.primary,
                        py: 1.5,
                        fontSize: '16px',
                        fontWeight: 600,
                        // boxShadow: `0 8px 16px ${colors.primary}4D`,
                        // '&:hover': {
                        //     bgcolor: colors.primary,
                        //     opacity: 0.9,
                        //     boxShadow: `0 12px 20px ${colors.primary}66`,
                        //     transform: 'translateY(-2px)'
                        // },
                        transition: 'all 0.3s ease'
                    }}
                >
                    Appliquer les filtres
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FilterDialog;
